import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ClipboardList, Package, ChefHat, Trash2, Warehouse, RefreshCw } from 'lucide-react';

const ACTION_ICONS = {
  create: { icon: Package, color: 'text-emerald-400', bg: 'bg-emerald-400/10', label: 'Creado' },
  update: { icon: RefreshCw, color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Editado' },
  delete: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-400/10', label: 'Eliminado' },
};

const RESOURCE_LABELS = {
  ingredient: 'Ingrediente',
  recipe: 'Receta',
  waste: 'Merma',
  stock: 'Stock',
};

export default function AuditLog() {
  const { axiosAuth } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosAuth().get('/audit-log');
        setLogs(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const filtered = filter === 'all' ? logs : logs.filter(l => l.resource_type === filter);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Trazabilidad</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Log de Auditoria</h2>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'ingredient', 'recipe', 'waste', 'stock'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`audit-filter-${f}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${filter === f ? 'bg-gold text-black font-bold' : 'border border-white/10 text-white/50 hover:border-white/30'}`}>
              {f === 'all' ? 'Todos' : RESOURCE_LABELS[f] || f}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="audit-stats">
        <div className="p-4 bg-[#0A0A0A] border border-white/5">
          <p className="text-2xl font-bold">{logs.length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Total Acciones</p>
        </div>
        <div className="p-4 bg-[#0A0A0A] border border-white/5">
          <p className="text-2xl font-bold text-emerald-400">{logs.filter(l => l.action === 'create').length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Creaciones</p>
        </div>
        <div className="p-4 bg-[#0A0A0A] border border-white/5">
          <p className="text-2xl font-bold text-blue-400">{logs.filter(l => l.action === 'update').length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Ediciones</p>
        </div>
        <div className="p-4 bg-[#0A0A0A] border border-white/5">
          <p className="text-2xl font-bold text-red-400">{logs.filter(l => l.action === 'delete').length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Eliminaciones</p>
        </div>
      </div>

      {/* Log entries */}
      <div className="space-y-2" data-testid="audit-entries">
        {filtered.map((log, i) => {
          const actionInfo = ACTION_ICONS[log.action] || ACTION_ICONS.create;
          const Icon = actionInfo.icon;
          return (
            <div key={log.id || i} className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-white/5 hover:border-white/10 transition-colors" data-testid={`audit-entry-${i}`}>
              <div className={`p-2 ${actionInfo.bg}`}>
                <Icon size={14} className={actionInfo.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{log.details}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${actionInfo.bg} ${actionInfo.color}`}>
                    {actionInfo.label}
                  </span>
                  <span className="text-[10px] text-white/30 uppercase tracking-widest">
                    {RESOURCE_LABELS[log.resource_type] || log.resource_type}
                  </span>
                  <span className="text-[10px] text-white/20">
                    {log.user_name}
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-white/40">{log.timestamp?.slice(0, 10)}</p>
                <p className="text-[10px] text-white/20">{log.timestamp?.slice(11, 19)}</p>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-center text-white/30 py-12 text-sm">No hay registros de auditoria</p>}
      </div>
    </div>
  );
}
